/**
 * DOCTOR-OS-B1-COCKPIT-V2-COMPONENTS -- the isolated Candidate B ("Cockpit")
 * view-model + PURE helpers.
 *
 * PROVENANCE (honest, per the founder "order IS the authority" ruling
 * 2026-08-08): the design source is the frozen candidate-b mockup set
 * (docs/design-studio/doctor-os/candidate-b/{case-sheet,doctors-day}.html +
 * ANATOMY-NOTES.md) and the frozen scenario (docs/design-studio/doctor-os/
 * SCENARIO.md). doctor-os Candidate B was left an OPEN FORK by DOCTOR-OS-
 * STUDIO-A0 (OPEN-QUESTIONS Q2/Q5/Q6); this package's order selects it. The
 * only RECORDED doctor verdict is the divergent "BUILD X (composed)" of the
 * sibling DOCTOR-WORKSPACE-DESIGN-STUDIO-V2 -- flagged for future
 * reconciliation, NOT cited here as authority.
 *
 * DATA CONTRACTS: every prop type mirrors a CANONICAL view-model shape that
 * exists on main today (frontend/src/dashboard/api.ts + types.ts wire
 * mirrors), cited field-by-field. No type is recreated, imported, or
 * predicted from CONVERSION-CONSEQUENCE-MINT-V1 (unmerged, separate lane).
 * Identity binds to ConsultPatientBanner / PatientClinicalProfileView present
 * on main; the fields that the mint merge may reshape are listed in the run
 * record's MINT-RECONCILIATION-REQUIRED section.
 *
 * ISOLATION: presentation only. Nothing here fetches, and no component built
 * on these types is mounted on a route or imported by a live surface.
 */

import type {
  CaseSheetConsentsWire,
  CaseSheetStatusView,
  ClinicalPlanItemView,
  ConsultPatientBanner,
  PatientClinicalProfileView,
  PlanItemStatusView,
  TreatmentCourseView,
} from '@/dashboard/api';
import type { AcBadgeTone } from '@/dashboard/ui';

/** The one decision axis (SCOPE: inline Accept / Decline / Defer per line).
 *  Every token is a real `treatment_plan_items.status` value
 *  (PlanItemStatusView, api.ts:3032 -- advised/accepted/declined/pending/
 *  deferred). */
export type PlanDecision = 'accept' | 'decline' | 'defer';

/** The plan-item status a decision maps to (used by an optional handler; the
 *  isolated components never write -- the future wiring package owns writes). */
export const PLAN_DECISION_STATUS: Readonly<Record<PlanDecision, PlanItemStatusView>> =
  Object.freeze({
    accept: 'accepted',
    decline: 'declined',
    defer: 'deferred',
  });

/** A line is DECIDABLE (renders the Accept/Decline/Defer control) only while
 *  it is undecided -- `advised` or `pending`. Decided lines render a status
 *  chip; completed lines render their completion. Mirrors the candidate-b
 *  ledger (ANATOMY-NOTES SCREEN 2: the ADVISED item carries the decision
 *  control in place; the rest render as status chips). */
export function planLineIsDecidable(status: PlanItemStatusView): boolean {
  return status === 'advised' || status === 'pending';
}

/** Course progress, mirroring lib/domain/clinical/treatment-journey.ts
 *  `CourseProgress` + `deriveCourseProgress` (only `accepted` items count
 *  toward accepted/completed; completed = accepted WITH completion evidence;
 *  remaining = accepted - completed). Re-derived here for display because the
 *  frontend never imports lib/domain (it binds to the api.ts wire mirrors);
 *  this is pure display derivation, never a second writer. */
export interface CockpitCourseProgress {
  readonly itemCount: number;
  readonly acceptedCount: number;
  readonly completedCount: number;
  readonly remainingPlannedCount: number;
}

export function deriveCockpitProgress(
  items: readonly Pick<ClinicalPlanItemView, 'status' | 'completedAt'>[],
): CockpitCourseProgress {
  const accepted = items.filter((i) => i.status === 'accepted');
  const completedCount = accepted.filter((i) => i.completedAt != null).length;
  return {
    itemCount: items.length,
    acceptedCount: accepted.length,
    completedCount,
    remainingPlannedCount: accepted.length - completedCount,
  };
}

const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/** "2026-06-12T..." -> "12 Jun". Deterministic (parses the ISO date parts; no
 *  clock read, no Intl locale) so a rendered ledger is pinnable. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return '';
  const month = SHORT_MONTHS[Number(m[2]) - 1] ?? '';
  return `${Number(m[3])} ${month}`.trim();
}

/** Whole-INR money formatter with Indian digit grouping (deterministic across
 *  node + chromium; no Intl locale dependency). estimated_value is money-of-
 *  record in WHOLE INR (SCENARIO s.3), so no minor-unit division here. */
export function formatInr(whole: number | null | undefined): string {
  if (whole == null) return 'Rs 0';
  const n = Math.round(Math.abs(whole));
  const s = n.toString();
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3);
  const grouped = rest
    ? `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}`
    : last3;
  return `Rs ${whole < 0 ? '-' : ''}${grouped}`;
}

/** A rendered teeth-strip chip derived from a plan line's free-text tooth_area
 *  + status (ANATOMY-NOTES SCREEN 2 teeth strip: per-quadrant treated /
 *  planned / advised). tooth_area is free-text (never an invented column). */
export interface TeethChip {
  readonly area: string;
  readonly label: string;
  readonly tone: AcBadgeTone;
}

export function deriveTeethChips(
  items: readonly Pick<ClinicalPlanItemView, 'toothArea' | 'status' | 'completedAt'>[],
): readonly TeethChip[] {
  const chips: TeethChip[] = [];
  for (const i of items) {
    const area = i.toothArea;
    if (!area) continue;
    if (i.completedAt != null) {
      chips.push({ area, label: `${area} - treated`, tone: 'positive' });
    } else if (i.status === 'accepted') {
      chips.push({ area, label: `${area} - planned`, tone: 'info' });
    } else if (i.status === 'advised') {
      chips.push({ area, label: `${area} - advised`, tone: 'pending' });
    } else {
      chips.push({ area, label: area, tone: 'neutral' });
    }
  }
  return chips;
}

/** The AcBadge tone for a decided / completed plan line's status chip.
 *  Colour pairs with the status-canon label (never colour alone); stays
 *  inside the seven-tone CHIP law. */
export function planLineChipTone(
  status: PlanItemStatusView,
  completed: boolean,
): AcBadgeTone {
  if (completed) return 'positive';
  switch (status) {
    case 'accepted':
      return 'brand';
    case 'declined':
      return 'danger';
    case 'deferred':
      return 'pending';
    case 'advised':
      return 'pending';
    default:
      return 'neutral';
  }
}

/** A graceful render state every region can be driven into (SCOPE STATES:
 *  dense + empty + error). `ready` renders the data; `empty` renders the S-4
 *  empty-state; `error` renders the S-8 error-state with the verbatim detail. */
export type RegionState = 'ready' | 'empty' | 'error';

/** The compact masthead NEXT dock (candidate-b case-sheet masthead: eyebrow +
 *  a derived one-line + one primary). A derived rendering choice over real
 *  fields, never a stored column. */
export interface CockpitNextDock {
  readonly eyebrow: string;
  readonly headline: string;
  readonly actionLabel: string;
}

/** Masthead model -- identity + allergy, bound to shapes present on main.
 *  patient: ConsultPatientBanner (api.ts:3430). clinicalProfile:
 *  PatientClinicalProfileView (api.ts:3516) -- allergy is a SEPARATE shape,
 *  never part of identity. Both are MINT-RECONCILIATION targets. */
export interface CockpitMastheadModel {
  readonly patient: ConsultPatientBanner;
  readonly clinicalProfile: PatientClinicalProfileView | null;
  readonly clinicalProfileAvailable: boolean;
  readonly visitStatus: CaseSheetStatusView;
  /** Human visit label e.g. "Visit 3" (derived from the appointment/course
   *  read; a display label, not a stored field). */
  readonly visitLabel: string;
  readonly next: CockpitNextDock | null;
}

/** Initials from the displayed name only (PHI-safe derivation, 03 P2). */
export function nameInitials(name: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

/** The allergy summary line rendered as a neutral chip in the masthead
 *  (ANATOMY-NOTES PROPOSED layer: no STRUCTURED allergy column exists; allergy
 *  lives in free-text Medical & Dental History, so the chip states the honest
 *  known state and never implies a structured field). */
export function allergyChipLabel(
  profile: PatientClinicalProfileView | null,
  available: boolean,
): string {
  if (!available || profile == null) return 'Allergy: not on file';
  const hasMedication =
    profile.medicationAllergies != null && profile.medicationAllergies.trim() !== '';
  if (profile.anaesthesiaAllergy === 'yes' || hasMedication) return 'Allergy: on file';
  // CW02/D06 (29 September): anaesthesia and medication are separate facts.
  // Blank medication text is unknown, even when anaesthesia is confirmed No.
  if (profile.anaesthesiaAllergy === 'no') return 'Allergy: partly recorded';
  return 'Allergy: not on file';
}

/** A prior-visit chronicle row (LAYER-2 visits, candidate-b right column
 *  "Earlier visits"). Mirrors the CaseSheetClinicalView note fields the doctor
 *  read carries; a display projection, never a new store. */
export interface EarlierVisitRow {
  readonly visitLabel: string;
  readonly dateLabel: string;
  readonly summary: string;
  readonly status: CaseSheetStatusView;
}

/** A related-list header (candidate-b right column: Appointments / Payments /
 *  Intake). Counts + a drill; the intake header is a SEAM (patient-reported,
 *  not a structured field today -- ANATOMY-NOTES PROPOSED layer). */
export interface RelatedListRow {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  /** SEAM = renders as honest text (not-yet-real drill), per the SEAM law. */
  readonly seam?: boolean;
}

/** The composed cockpit model -- the single typed prop the composition takes.
 *  Every field mirrors a canonical shape cited above. */
export interface DoctorCockpitModel {
  readonly masthead: CockpitMastheadModel;
  /** The visit's appointment id -- the ONLY prop the shared intake block needs
   *  (ConsultIntakeQuestionsBlockProps.appointmentId). */
  readonly appointmentId: number;
  /** Whether the pre-consult intake handoff is satisfied (collapses to the
   *  "Clinical intake complete" line). */
  readonly intakeComplete: boolean;
  readonly examinationFindings: string | null;
  readonly diagnosis: string | null;
  readonly course: TreatmentCourseView;
  readonly planItems: readonly ClinicalPlanItemView[];
  /** Billing-plane display value (whole INR); the payments plane is separate
   *  from these clinical types (SCENARIO s.3 "Paid all visits Rs 12,000"). */
  readonly paidAllVisitsWholeInr: number | null;
  readonly consents: CaseSheetConsentsWire | null;
  readonly evidenceCount: number;
  readonly evidenceLabel: string;
  readonly followUpOption: string | null;
  readonly followUpNote: string | null;
  readonly canClose: boolean;
  readonly earlierVisits: readonly EarlierVisitRow[];
  readonly relatedLists: readonly RelatedListRow[];
}
